import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Entities_Modules_Module } from '@suntex.fim/api-client';
import { FavoriteGamesAction } from '@suntex.fim/enums';
import { ModuleTagging } from '@suntex.fim/framework';

import { useFavoriteModules } from './useFavoriteModules';

const SEARCH_DEBOUNCE_MS = 300;

export interface UseModuleSearchResult {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  debouncedSearchTerm: string;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: Dispatch<SetStateAction<boolean>>;
  isFavorite: (slug: string, variant: number | undefined) => boolean;
  toggleFavorite: (
    slug: string,
    variant: number | undefined,
    action: FavoriteGamesAction.Add | FavoriteGamesAction.Remove,
  ) => Promise<void>;
  /** Returns true if a module passes all active filters (search, favorites).
   *  @param slug - The module slug to check
   *  @param extraText - Optional additional text to include in the search match
   *    (e.g., tree-node-specific label or metadata in ModuleCatalog)
   *  @param variant - Optional module variant used by the favorites filter
   */
  matchesSearch: (
    slug: string,
    variant: number | undefined,
    extraText?: string,
  ) => boolean;
}

/**
 * Unified search and favorites filter state for module views.
 * Shared by `ModuleCatalog` (tree) and `ContentLibrary` (card grid).
 *
 * Search covers module name, description, ariaDescription, slug, and all
 * associated tag paths. An `extraText` param lets tree views include
 * node-level metadata (label, tag, info) in the search match.
 */
export function useModuleSearch(
  modules: readonly Entities_Modules_Module[],
  moduleTagging: readonly ModuleTagging[],
): UseModuleSearchResult {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const { isFavorite, toggleFavorite } = useFavoriteModules();

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchTerm(searchTerm),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pre-compute searchable text per module slug (module fields + tag paths).
  const moduleSearchTexts = useMemo(() => {
    // Group tag paths by module slug once — O(moduleTagging)
    const tagsBySlug = new Map<string, string[]>();
    for (const t of moduleTagging) {
      const slug = t.module.slug;
      const existing = tagsBySlug.get(slug);
      if (existing) {
        existing.push(t.tag.path);
      } else {
        tagsBySlug.set(slug, [t.tag.path]);
      }
    }

    const map = new Map<string, string>();
    for (const m of modules) {
      const tagPaths = tagsBySlug.get(m.slug)?.join('\n') ?? '';
      map.set(
        m.slug,
        [m.name, m.description, m.ariaDescription, m.slug, tagPaths]
          .filter(Boolean)
          .join('\n')
          .toLowerCase(),
      );
    }
    return map;
  }, [modules, moduleTagging]);

  const matchesSearch = useCallback(
    (
      slug: string,
      variant: number | undefined,
      extraText?: string,
    ): boolean => {
      if (showFavoritesOnly && !isFavorite(slug, variant)) return false;

      const normalizedSearch = debouncedSearchTerm.trim().toLowerCase();
      if (normalizedSearch) {
        const precomputed = moduleSearchTexts.get(slug) ?? '';
        const extra = extraText?.toLowerCase() ?? '';
        if (
          !precomputed.includes(normalizedSearch) &&
          !extra.includes(normalizedSearch)
        ) {
          return false;
        }
      }

      return true;
    },
    [showFavoritesOnly, isFavorite, debouncedSearchTerm, moduleSearchTexts],
  );

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    showFavoritesOnly,
    setShowFavoritesOnly,
    isFavorite,
    toggleFavorite,
    matchesSearch,
  };
}
